<template>
  <div class="min-h-screen bg-white container mx-auto px-4 sm:px-6 lg:px-8 mb-10">

    <portal to="mobile-header" v-slot="{didToggle, toggle}">
      <div class="flex-grow min-w-0 flex flex-col align-center items-center justify-center h-12">
        <div class="align-center text-lg font-bold px-1 font-display inline-flex sm:hidden text-center truncate">
            Students & Classes
        </div>
      </div>
    </portal>


    <header class="hidden sm:block py-10">
      <div class="md:flex md:items-center md:justify-between max-w-7xl mx-auto">
        <div class="flex-1 min-w-0">
          <h2 class="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:leading-9 sm:truncate">
            Students & Classes
          </h2>
        </div>
        <div class="mt-4 flex md:mt-0 md:ml-4">
          <span class="ml-3 shadow-sm rounded-md">
            <RouterLink :to="{name: 'studentDataloads'}" tag="button" type="button" class="inline-flex items-center px-4 py-2 border border-transparent text-sm leading-5 font-medium rounded-md text-white bg-cardstock-green-600 hover:bg-cardstock-green-500 focus:outline-none focus:ring-cardstock-green focus:border-cardstock-green-700 active:bg-cardstock-green-700 transition duration-150 ease-in-out cursor-pointer">
              Load Student Data
            </RouterLink>
          </span>
        </div>
      </div>
    </header>

    <main class="mt-5 sm:mt-0">
      <div class="max-w-7xl mx-auto shadow-lg rounded sm:rounded-lg border border-gray-200">

        <router-link :to="{name: 'studentsAll'}" class="block hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition duration-150 ease-in-out overflow-hidden rounded sm:rounded-lg px-4 py-5 sm:p-6">
            <div class="flex items-center">
              <svg class="h-5 w-5 mr-5 flex-shrink-0 text-gray-400" fill="currentColor" viewBox="0 0 20 20">

              </svg>
              <div class="min-w-0 flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <div class="text-lg leading-5 font-medium text-cardstock-blue-500 truncate">
                    All Students at {{team.name}}
                  </div>
                  <div class="mt-2 flex">
                    <div class="flex items-center text-sm leading-5 text-gray-500">
                      <span>
                        {{team.studentCount}} students
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="ml-5 flex-shrink-0">
                <svg class="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
                </svg>
              </div>
            </div>
        </router-link>

      </div>
    </main>

    <header class="hidden sm:block py-5 mt-6">
      <div class="md:flex md:items-center md:justify-between max-w-7xl mx-auto">
        <div class="flex-1 min-w-0">
          <h2 class="text-xl leading-7 text-gray-900 sm:text-lg sm:leading-9 sm:truncate">
            Your Classes and Groups
          </h2>
        </div>
        <RouterLink :to="{name: 'groupNew', params: { canGoBack: true}}" tag="button" type="button" class="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs leading-4 font-medium rounded text-cardstock-green-700 bg-cardstock-green-100 hover:bg-cardstock-green-50 focus:outline-none focus:border-cardstock-green-300 focus:ring-cardstock-green active:bg-cardstock-green-200 transition ease-in-out duration-150">
          New Group
        </RouterLink>
      </div>
    </header>

    <main class="mt-5 sm:mt-0">
      <div class="max-w-7xl mx-auto shadow-lg rounded sm:rounded-lg">
        <SortableGroups :groups="groups" v-slot="{ group, dragging }">
          <div :key="group.id" class="
              first:rounded-t sm:first:rounded-t-lg
              last:rounded-b sm:last:rounded-b-lg
              border-t border-l border-r
              border-b
              border-gray-200 -mt-px first:mt-0">
            <router-link
              :to="{name: 'group', params: {groupId: group.id}}"
              class="block focus:outline-none focus:bg-white transition duration-150 ease-in-out overflow-hidden
              px-4 py-5 sm:p-6
              first:rounded-t sm:first:rounded-t-lg
              last:rounded-b sm:last:rounded-b-lg"
              :class="[dragging ? '' : 'hover:bg-gray-50']">
                <div class="flex items-center">
                  <svg class="h-5 w-5 mr-5 flex-shrink-0 text-gray-400 handle cursor-grab" fill="currentColor" viewBox="0 0 20 20" @click.prevent>
                    <font-awesome-icon :icon="['far', 'grip-lines']" />
                  </svg>
                  <div class="flex flex-col justify-center" v-if="group.icon">
                      <svg
                      class="w-10 h-10 p-2 flex items-center justify-center text-lg rounded shadow mr-3"
                      :class="[group.color ? `bg-${group.color.name} text-white` : 'border border-cool-gray-200 text-black']" fill="currentColor" viewBox="0 0 20 20" @click.prevent>
                        <use :href="`/images/regular.svg#${group.icon.name}`"></use>
                      </svg>
                  </div>
                  <div class="min-w-0 flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <div class="text-lg leading-5 font-medium text-cardstock-blue-500 truncate">
                        {{group.name}}
                      </div>
                      <div class="mt-2 flex">
                        <div class="flex items-center text-sm leading-5 text-gray-500">
                          <span>
                            {{group.students.length}} students
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="ml-5 flex-shrink-0">
                    <svg class="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
                    </svg>
                  </div>
                </div>
            </router-link>
          </div>
        </SortableGroups>
      </div>
    </main>

    <header class="hidden sm:block py-5 mt-6">
      <div class="md:flex md:items-center md:justify-between max-w-7xl mx-auto">
        <div class="flex-1 min-w-0">
          <h2 class="text-xl leading-7 text-gray-900 sm:text-lg sm:leading-9 sm:truncate">
            Grade Levels
          </h2>
        </div>
        <RouterLink :to="{name: 'gradeLevelNew'}" tag="button" type="button" class="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs leading-4 font-medium rounded text-cardstock-green-700 bg-cardstock-green-100 hover:bg-cardstock-green-50 focus:outline-none focus:border-cardstock-green-300 focus:ring-cardstock-green active:bg-cardstock-green-200 transition ease-in-out duration-150">
          New Grade Level
        </RouterLink>
      </div>
    </header>

    <main class="mt-5 sm:mt-0">
      <div class="max-w-7xl mx-auto shadow-lg rounded sm:rounded-lg">
        <SortableGradeLevels :gradeLevels="gradeLevels" v-slot="{ gradeLevel, dragging }">
          <div :key="gradeLevel.id" class="
              first:rounded-t sm:first:rounded-t-lg
              last:rounded-b sm:last:rounded-b-lg
              border-t border-l border-r
              border-b
              border-gray-200 -mt-px first:mt-0">
            <router-link
              :to="{name: 'gradeLevel', params: {gradeLevelId: gradeLevel.id}}"
              class="block focus:outline-none focus:bg-white transition duration-150 ease-in-out overflow-hidden
              px-4 py-5 sm:p-6
              first:rounded-t sm:first:rounded-t-lg
              last:rounded-b sm:last:rounded-b-lg"
              :class="[dragging ? '' : 'hover:bg-gray-50']">
                <div class="flex items-center">
                  <svg class="h-5 w-5 mr-5 flex-shrink-0 text-gray-400 handle cursor-grab" fill="currentColor" viewBox="0 0 20 20" @click.prevent>
                    <font-awesome-icon :icon="['far', 'grip-lines']" />
                  </svg>
                  <div class="flex flex-col justify-center" v-if="gradeLevel.icon">
                      <svg
                      class="w-10 h-10 p-2 flex items-center justify-center text-lg rounded shadow mr-3"
                      :class="[gradeLevel.color ? `bg-${gradeLevel.color.name} text-white` : 'border border-cool-gray-200 text-black']" fill="currentColor" viewBox="0 0 20 20" @click.prevent>
                        <use :href="`/images/regular.svg#${gradeLevel.icon.name}`"></use>
                      </svg>
                  </div>
                  <div class="min-w-0 flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <div class="text-lg leading-5 font-medium text-cardstock-blue-500 truncate">
                        {{gradeLevel.name}}
                      </div>
                      <div class="mt-2 flex">
                        <div class="flex items-center text-sm leading-5 text-gray-500">
                          <span>
                            {{gradeLevel.studentCount}} students
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="ml-5 flex-shrink-0">
                    <svg class="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
                    </svg>
                  </div>
                </div>
            </router-link>
          </div>
        </SortableGradeLevels>
      </div>
    </main>
    <RouterView/>
  </div>
</template>

<script>
import { preloadMany }  from '@/preloader'
import { tz }           from '@/tz'

import SortableGroups   from '@/components/SortableGroups.vue'
import SortableGradeLevels   from '@/components/SortableGradeLevels.vue'

import GradeLevelsQuery from '@/queries/GradeLevels.gql'
import TeamQuery from '@/queries/Team.gql'
import GroupsQuery from '@/queries/Groups.gql'
import GroupDeleteMutation from '@/mutations/GroupDelete.gql'
import GroupCleanseMutation from '@/mutations/GroupCleanse.gql'

const preloadQueries = (to) => {
  return [
    {
      query: GroupsQuery,
      queryName: 'groups',
      variables: {
        tz: tz(),
        teamId: to.params.teamId,
        forCurrent: true
      },
      fetchPolicy: 'network-only'
    },
    {
      query: GradeLevelsQuery,
      queryName: 'gradeLevels',
      variables: {
        teamId: to.params.teamId
      },
      fetchPolicy: 'network-only'
    },
    {
      query: TeamQuery,
      queryName: 'team',
      variables: {
        teamId: to.params.teamId
      },
      fetchPolicy: 'network-only'
    },
  ]
}


export default {
  components: {
    SortableGradeLevels,
    SortableGroups,
  },

  props: {
    teamId: {
      required: true
    }
  },

  beforeRouteEnter(to, from, next) {
    preloadMany({
      queries: preloadQueries(to),
      callback: next
    })
  },

  beforeRouteUpdate(to, from, next) {
    preloadMany({
      queries: preloadQueries(to),
      callback: next,
      mountedVm: this
    })
  },

  computed: {
    needsBlankSlate() {
      return false
    }
  },

  apollo: {
    gradeLevels: {
      query: GradeLevelsQuery,
      queryName: 'gradeLevels',
      variables() {
        return { teamId: this.teamId }
      },
      fetchPolicy: 'cache-first'
    },
    groups: {
      query: GroupsQuery,
      variables() {
        return {
          tz: tz(),
          teamId: this.teamId,
          forCurrent: true
        }
      },
      fetchPolicy: 'cache-first'
    },
    team: {
      query: TeamQuery,
      variables() {
        return {
          teamId: this.teamId,
        }
      },
      fetchPolicy: 'cache-first'
    }
  }

}
</script>
